import { useMemo } from 'react';
import { Col, Row, Card, Button } from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderOffline, renderError } from '../components/render_helpers';
import ReactTable from '../components/react_table/react_table';
import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import {
  manufacturerListPageQuery,
  manufacturerDelete as manufacturerDeleteMutation,
} from '../graphql/manufacturer_queries';
import { settingsSet } from '../store/settings_slice';
import * as updateFunctions from '../update_functions';

const ManufacturerList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const [manufacturerDelete] = useMutation(manufacturerDeleteMutation);

  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
    client: pageClient,
  } = useQuery(manufacturerListPageQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ manufacturerList }) => {
      // write an individual query for every manufacturer in the list
      updateFunctions.manufacturerList(pageClient, manufacturerList);
    },
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const manufacturerDeleteClicked = async (e) => {
    const dataId = e.currentTarget.getAttribute('data-id');
    const manufacturerId = uuidValidate(dataId)
      ? dataId
      : parseInt(e.currentTarget.getAttribute('data-id'), 10);
    const mutationData = {
      variables: { id: manufacturerId },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'ManufacturerType',
        recordId: manufacturerId,
        mutationType: 'DELETE',
      },
      update: updateFunctions.manufacturerDelete,
    };
    mutationData.optimisticResponse = updateFunctions.optimistic(
      'manufacturerDelete',
      mutationData
    );
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await manufacturerDelete(mutationData);
        toastSuccess('Manufacturer delete ok');
      } catch (err) {
        console.log(err.toString());
        toastError('Manufacturer delete failed');
      } finally {
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
      }
    } else {
      manufacturerDelete(mutationData);
      toastWarning(`Manufacturer delete ok locally. Go online to make permanent`);
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
    }
  };

  const parentColumns = [
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
  ];

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm={12}>
          <div className="float-none">
            <div className="float-start">
              <h1 className="h3 mb-3">Manufacturers</h1>
            </div>
            <div className="float-end">
              <Button
                className="me-2"
                variant="primary"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                Refresh
              </Button>
              <LinkContainer to="/manufacturers/new">
                <Button variant="primary">New Manufacturer</Button>
              </LinkContainer>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <ReactTable
                rootName="manufacturer"
                parentColumns={parentColumns}
                data={pageData.manufacturerList}
                doShow={false}
                doDelete={currentUser?.perms?.administrator}
                handleDelete={manufacturerDeleteClicked}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default ManufacturerList;
